import { PermissionGate } from '@/components/auth/PermissionGate'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { RootLayout, AuthLayout, DashboardLayout } from '@/components/layout'
import { ProtectedRoute, OnboardingRoute } from '@/components/shared'

// Auth pages
import { LoginPage, SignupPage, ForgotPasswordPage, LogoutPage } from '@/pages/auth'

// Onboarding pages
import { OnboardingPage, CreateFamilyWizard, PendingInvitationsPage } from '@/pages/onboarding'

// Public invite page
import { AcceptInvitePage } from '@/pages/invite'

// Dashboard pages
import { CalendarPage } from '@/pages/calendar/CalendarPage'
import { DashboardPage } from '@/pages/dashboard'
import { HealthOverviewPage, HealthDetailPage } from '@/pages/health'
import { FinancialOverviewPage, TransactionsPage } from '@/pages/financial'
import { AlertsPage } from '@/pages/alerts/AlertsPage'
import { FamilyPage } from '@/pages/family/FamilyPage'
import { ReportsPage } from '@/pages/reports/ReportsPage'
import { SettingsPage } from '@/pages/settings/SettingsPage'
import { ChatPage } from '@/pages/chat/ChatPage'

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      // Public auth routes
      {
        element: <AuthLayout />,
        children: [
          { path: 'login', element: <LoginPage /> },
          { path: 'signup', element: <SignupPage /> },
          { path: 'forgot-password', element: <ForgotPasswordPage /> },
        ],
      },

      // Logout route - clears all state and redirects to login
      {
        path: 'logout',
        element: <LogoutPage />,
      },

      // Public invite acceptance page (can view without auth, auth required to accept)
      {
        path: 'invite/:invitationId',
        element: <AcceptInvitePage />,
      },

      // Onboarding routes (protected but skip family check)
      {
        path: 'onboarding',
        element: <OnboardingRoute><OnboardingPage /></OnboardingRoute>,
      },
      {
        path: 'onboarding/create-family',
        element: <OnboardingRoute><CreateFamilyWizard /></OnboardingRoute>,
      },
      {
        path: 'onboarding/invitations',
        element: <OnboardingRoute><PendingInvitationsPage /></OnboardingRoute>,
      },

      // Protected dashboard routes
      {
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'calendar', element: <CalendarPage /> },

          // Health
          { path: 'health', element: <PermissionGate permission="view_health" fallback={<p>You do not have access to this page.</p>}><HealthOverviewPage /></PermissionGate> },
          { path: 'health/member/:memberId', element: <PermissionGate permission="view_health" fallback={<p>You do not have access to this page.</p>}><HealthDetailPage /></PermissionGate> },

          // Financial
          { path: 'financial', element: <PermissionGate permission="view_financial" fallback={<p>You do not have access to this page.</p>}><FinancialOverviewPage /></PermissionGate> },
          { path: 'financial/transactions', element: <PermissionGate permission="view_financial" fallback={<p>You do not have access to this page.</p>}><TransactionsPage /></PermissionGate> },

          // Alerts
          { path: 'alerts', element: <AlertsPage /> },
          { path: 'alerts/:alertId', element: <AlertsPage /> },

          // Family
          { path: 'family', element: <FamilyPage /> },

          // Reports
          { path: 'reports', element: <PermissionGate roles={["PRIMARY_CAREGIVER", "CAREGIVER"]} fallback={<p>Reports are available to caregivers.</p>}><ReportsPage /></PermissionGate> },

          // Chat
          { path: 'chat', element: <ChatPage /> },

          // Settings
          { path: 'settings', element: <SettingsPage /> },
        ],
      },
    ],
  },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}
